import { useState } from "react";
import { Form, Accordion, Button, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { restApiBase } from "../settings";
import { ScopePopover } from "./ScopePopover";

export function ProjectCorporate({ userId, token }) {
    const scopes = [
        {
            name: "Competitive Strategy",
            description:
                "Analyze corporate risk and investment attractiveness through the lens of the company's corporate strategy. Evaluate this in context of the industry in which it operates and competes.",
        },
        {
            name: "Financials",
            description:
                "Assess corporate risk and investment attractiveness, paying particular attention to the company's financial statements.",
        },
        {
            name: "Stock Performance",
            description:
                "Assess corporate risk and investment attractiveness, emphasizing its equity valuation using conventional measures.",
        },
        {
            name: "Debt Performance",
            description:
                "Assess corporate risk and investment attractiveness, emphasizing its debt securities and leverage metrics.",
        },
    ];

    const categories = {
        "Special Risk Factors and Situations to Focus On": [
            "Debt Dynamics",
            "Sales Dynamics",
            "Costs Dynamics",
            "Accounting",
            "New Products",
            "New Markets",
            "Competitive Landscape",
            "Technology and AI",
        ],
    };

    const [companySymbol, setCompanySymbol] = useState("");
    const [selectedScope, setSelectedScope] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [showWarning, setShowWarning] = useState(null);
    const [processing, setProcessing] = useState(false);

    const navigate = useNavigate();

    const toggleScope = (scope) => {
        setSelectedScope((prev) =>
            prev.includes(scope)
                ? prev.filter((s) => s !== scope)
                : [...prev, scope]
        );
    };

    const toggleCategory = (item) => {
        setSelectedCategories((prev) =>
            prev.includes(item)
                ? prev.filter((i) => i !== item)
                : [...prev, item]
        );
    };

    const allowRun = companySymbol.trim().length > 0 && selectedScope.length > 0;

    const runReport = () => {
        if (!allowRun) {
            setShowWarning("Please select at least one scope and enter a valid company name or symbol.");
            return;
        }

        setProcessing(true);
        setShowWarning(null);

        const requestData = {
            report_type: "SAR_CORPORATE",
            parameters: {
                target: companySymbol,
                scope: selectedScope,
                categories: selectedCategories,
            },
            options: {
                risk_factors: selectedCategories,
            },
        };

        fetch(`${restApiBase}run_report?user_id=${userId}`, {
            method: "POST",
            headers: {
                Authorization: "Bearer " + token,
                "Content-Type": "application/json;charset=utf-8",
            },
            body: JSON.stringify(requestData),
        })
            .then((res) => res.json())
            .then((result) => {
                setProcessing(false);
                if (result?.report_id) {
                    navigate(`/report/run/${result.report_id}`);
                } else if (result?.error) {
                    setShowWarning(`${result.error}: ${result.error_description}`);
                }
            })
            .catch((err) => {
                setProcessing(false);
                setShowWarning(`Network error: ${err.message}`);
            });
    };

    return (
        <div className="row">
            <div className="col">
                <h3 className="mb-3">Corporate Analysis</h3>

                <Form.Group className="mb-3">
                    <Form.Label>Enter Company Name or Symbol</Form.Label>
                    <Form.Control
                        type="text"
                        value={companySymbol}
                        onChange={(e) => setCompanySymbol(e.target.value)}
                        placeholder=""
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Scope of Analysis</Form.Label>
                    <div>
                        {scopes.map(({ name, description }) => (
                            <div
                                key={name}
                                className="d-inline-flex align-items-center me-3 mb-2"
                            >
                                <Form.Check
                                    label={name}
                                    type="checkbox"
                                    checked={selectedScope.includes(name)}
                                    onChange={() => toggleScope(name)}
                                />
                                <ScopePopover name={name} description={description} />
                            </div>
                        ))}
                    </div>
                </Form.Group>

                <Accordion className="mb-3">
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>Special Risk Factors and Situations to Focus On</Accordion.Header>
                        <Accordion.Body>
                            <div className="d-flex flex-wrap gap-2">
                                {categories["Special Risk Factors and Situations to Focus On"].map((item) => (
                                    <Form.Check
                                        key={item}
                                        inline
                                        label={item}
                                        type="checkbox"
                                        checked={selectedCategories.includes(item)}
                                        onChange={() => toggleCategory(item)}
                                    />
                                ))}
                            </div>
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>

                {showWarning && (
                    <Alert variant="danger" dismissible onClose={() => setShowWarning(null)}>
                        {showWarning}
                    </Alert>
                )}

                <div className="my-4">
                    <Button disabled={!allowRun || processing} onClick={runReport}>
                        {processing ? "Processing..." : "Run report"}
                    </Button>
                </div>
            </div>
        </div>
    );
}
